"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CreateEffectModal from "../components/dashboard/CreateEffectModal";
import {
  Sparkles,
  Send,
  ImageIcon,
  Edit3,
  Video,
  Calendar,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import EffectsSection from "../components/dashboard/EffectsSection";
import FollowingPosts from "../components/dashboard/FollowingPosts";
import PhotoEffectsComponent from "../components/dashboard/PhotoEffectsComponent";
import type { GeneratorType } from "./CreatePage";
import ContentModal from "../components/ContentModal";
import dreamWorldProvider from "../DreamWorldProvider";
import { LibraryItem } from "../types/library";
import PageLayout from "../components/ui/PageLayout";

const DashboardPage: React.FC = () => {
  const [selectedContent, setSelectedContent] = useState<LibraryItem | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateEffectModal, setShowCreateEffectModal] = useState(false);
  const [contentSource, setContentSource] = useState<"library" | "following">(
    "library"
  );

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("hasSeenCreateEffectModal");
    if (!hasSeenModal) {
      setShowCreateEffectModal(true);
    }
  }, []);

  const handleCloseCreateEffectModal = () => {
    localStorage.setItem("hasSeenCreateEffectModal", "true");
    setShowCreateEffectModal(false);
  };

  const handleContentClick = (
    content: LibraryItem,
    source: "library" | "following"
  ) => {
    setSelectedContent(content);
    setContentSource(source);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContent(null);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [prompt, setPrompt] = useState("");
  const navigate = useNavigate();

  const handleGenerateClick = () => {
    if (prompt.trim()) {
      navigate("/create", { state: { prompt } });
    }
  };

  const features = [
    {
      id: 1,
      title: "AI editing",
      description: "Create stunning AI-generated images edits",
      icon: Edit3,
      gradient: "from-purple-500 to-pink-500",
      generatorType: "image-editing" as GeneratorType,
    },
    {
      id: 2,
      title: "Video",
      description: "Generate dynamic videos and animations",
      icon: Video,
      gradient: "from-blue-500 to-cyan-500",
      generatorType: "video" as GeneratorType,
    },
    {
      id: 3,
      title: "Image",
      description: "Create stunning AI-generated images",
      icon: ImageIcon,
      gradient: "from-green-500 to-emerald-500",
      generatorType: "image" as GeneratorType,
    },
    {
      id: 4,
      title: "Effects",
      description: "Create your own effects",
      icon: Sparkles,
      gradient: "from-yellow-500 to-orange-500",
      generatorType: "effects" as GeneratorType,
    },
    {
      id: 5,
      title: "event",
      description: "Create community focused events",
      icon: Calendar,
      gradient: "from-indigo-500 to-purple-500",
      generatorType: "event" as GeneratorType,
    },
  ];

  const handleFeatureClick = (feature: (typeof features)[number]) => {
    navigate("/create", {
      state: {
        activeGenerator: feature.generatorType,
      },
    });
  };
  const goToEventsPage = () => {
    navigate("/events");
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full min-h-[8rem] md:max-h-40 overflow-hidden"
      >
        <img
          src="https://images.pexels.com/photos/2532003/pexels-photo-2532003.jpeg"
          alt="DreamWorld 2.1 Event Banner"
          className="w-full h-full md:h-40 object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-between px-2 md:px-2">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-white mb-1">
              DreamWorld September Event: Design a Home Interior
            </h2>
            <p className="text-sm md:text-base text-gray-200">
              You are given an empty home interior. Make some edits with
              dreamworld edit tool, and Submit your creation before the end of
              the month.
            </p>
          </div>
          <button
            onClick={goToEventsPage}
            className="bg-black/50 hover:bg-black/70 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-medium transition-all duration-300"
          >
            Enter event
          </button>
        </div>
      </motion.div>

      <PageLayout>
        <div className="w-full text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 bg-clip-text text-transparent py-1"
          >
            What are you going to create today?
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base"
          >
            Unleash your imagination. Describe anything, and watch our AI bring
            it to life in stunning images and videos.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          <div className="relative">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleGenerateClick()}
              placeholder="A majestic lion wearing a crown in a futuristic city..."
              className="w-full pl-5 pr-20 py-3 bg-gray-800/50 border-2 border-gray-700/50 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-300 text-white placeholder-gray-500"
            />
            <motion.button
              onClick={handleGenerateClick}
              disabled={!prompt.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: prompt.trim() ? 1.1 : 1 }}
              whileTap={{ scale: prompt.trim() ? 0.95 : 1 }}
            >
              <Send size={18} />
            </motion.button>
          </div>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="w-full"
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center gap-1 bg-gray-800/30 rounded-2xl p-2 backdrop-blur-sm border border-gray-700/30">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.button
                    onClick={() => handleFeatureClick(feature)}
                    key={feature.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                    className="flex-1 group cursor-pointer rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-700/50"
                  >
                    <div className="flex flex-col items-center justify-center p-3 md:p-4">
                      <div
                        className={`p-2 rounded-lg bg-gradient-to-br ${feature.gradient} mb-2 group-hover:scale-110 transition-transform duration-300`}
                      >
                        <IconComponent
                          size={16}
                          className="text-white md:w-5 md:h-5"
                        />
                      </div>
                      <h3 className="text-xs md:text-sm font-medium text-white group-hover:text-gray-200 transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <ChevronDown
                        size={12}
                        className="text-gray-500 mt-1 md:w-4 md:h-4"
                      />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.section>

        <EffectsSection />
        <PhotoEffectsComponent />
        <FollowingPosts onContentClick={handleContentClick} />
      </PageLayout>
      <AnimatePresence>
        {selectedContent && (
          <ContentModal
            contentId={selectedContent.id}
            source={contentSource}
            onClose={handleCloseModal}
            onLike={(id) => {}}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCreateEffectModal && (
          <CreateEffectModal onClose={handleCloseCreateEffectModal} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardPage;
